import { useCallback, useEffect, useState } from 'react';

/**
 * Light / dark switch for the long forms (profile onboarding, the 36Q
 * Portrait, the deep questionnaire, register, login) — the screens where a
 * person types for ten minutes and readability is comfort.
 *
 * Scoped, not global: the `light` class goes on <html> only while a form page
 * that calls this hook is mounted, and is removed on unmount, so the rest of
 * the app keeps the navy-and-gold brand. Default is dark, the brand look:
 * following the phone's own setting put new members straight into the light
 * version, which is the less finished of the two (founder, Sep 19 2026). A
 * tap on the toggle switches to light and is remembered on this device.
 */
const STORAGE_KEY = 'ss_form_theme';
const LIGHT_CLASS = 'light';

export function readStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function systemTheme() {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export default function useFormTheme() {
  const [theme, setTheme] = useState(() => readStoredTheme() || 'dark');

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle(LIGHT_CLASS, theme === 'light');
    return () => root.classList.remove(LIGHT_CLASS);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
      return next;
    });
  }, []);

  return { theme, toggle };
}
